import React, { useEffect, useLayoutEffect, useRef } from 'react';

const ScrollableTable = ({
  children,
  className = '',
  storageKey = 'agents_table_scroll',
}) => {
  const scrollerRef = useRef(null);
  const contentRef = useRef(null); // wraps children
  const lastLeftRef = useRef(0);
  const userActiveHScrollRef = useRef(false);
  const cancelIdRef = useRef(null);
  const idleTimerRef = useRef(null);

  //  Restore previous horizontal position on mount
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const saved = sessionStorage.getItem(storageKey);
    const target = saved ? parseInt(saved, 10) || 0 : 0;

    let tries = 0;
    const apply = () => {
      if (!el) return;
      el.scrollLeft = target;
      lastLeftRef.current = el.scrollLeft;
      tries += 1;
      if (tries < 6) {
        cancelIdRef.current = requestAnimationFrame(apply);
      }
    };
    apply();

    return () => {
      if (cancelIdRef.current) cancelAnimationFrame(cancelIdRef.current);
    };
  }, [storageKey]);

  // Persist on scroll and detect user horizontal gesture
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    const onScroll = () => {
      // mark user is actively scrolling horizontally
      userActiveHScrollRef.current = true;
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(
        () => (userActiveHScrollRef.current = false),
        250
      );

      // persist
      sessionStorage.setItem(storageKey, String(el.scrollLeft));
      lastLeftRef.current = el.scrollLeft;
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      // save one last time
      sessionStorage.setItem(storageKey, String(lastLeftRef.current));
    };
  }, [storageKey]);


  useEffect(() => {
    const el = scrollerRef.current;
    const content = contentRef.current;
    if (!el || !content) return;


    const ro = new ResizeObserver(() => {
      if (userActiveHScrollRef.current) return;


      const saved = sessionStorage.getItem(storageKey);
      const target = saved ? parseInt(saved, 10) || 0 : lastLeftRef.current;


      requestAnimationFrame(() => {
        if (el.scrollWidth > el.clientWidth) {
          const maxLeft = el.scrollWidth - el.clientWidth;
          const clamped = Math.max(0, Math.min(target, maxLeft));
          if (Math.abs(el.scrollLeft - clamped) > 1) {
            el.scrollLeft = clamped;
            lastLeftRef.current = clamped;
            sessionStorage.setItem(storageKey, String(clamped));
          }
        }
      });
    });

    ro.observe(content);
    return () => ro.disconnect();
  }, [storageKey]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    let rafId;
    const tick = () => {
      // skip while user is actively scrolling
      if (!userActiveHScrollRef.current) {
        const last = lastLeftRef.current;
        if (last > 0 && el.scrollLeft < last - 1) {
          // Snap-back detected → restore
          const maxLeft = el.scrollWidth - el.clientWidth;
          el.scrollLeft = Math.max(0, Math.min(last, maxLeft));
        }
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(rafId);
  }, []);

  return (
    <div
      className={`table-container ${className}`}
      style={{ minWidth: 0, width: '100%' }}
    >
      <div
        ref={scrollerRef}
        className="table-wrapper"
        style={{
          overflowX: 'auto',
          overflowY: 'hidden',
          minWidth: 0,
          width: '100%',
          overscrollBehaviorX: 'contain',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <div ref={contentRef} style={{ display: 'inline-block', minWidth: '100%' }}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default ScrollableTable